import { useEffect, useState } from 'react';
import { getSettingsAccess, SettingsAccess } from '../../crud/management_api/settings_delegation';
import { isFeatureRolloutLocal, STAFF_TIPS_REFRESH_MS } from '../../configuration/directMenuRollout';

const denied: SettingsAccess = { read: false, edit: false };
type State = SettingsAccess & { status: 'loading' | 'ready' | 'error'; localId: number; userId: number };

/** Refresh scoped grants only for eligible non-superadmins, fail closed on errors. */
export function useSettingsDelegationAccess(localId: number, userId: number, superadmin: boolean): State {
  const [state, setState] = useState<State>({ ...denied, status: 'loading', localId, userId });
  useEffect(() => {
    if (!isFeatureRolloutLocal(localId) || superadmin) return;
    setState({ ...denied, status: 'loading', localId, userId });
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const load = async () => {
      if (document.hidden || disposed) return;
      const request = new AbortController();
      controller = request;
      try {
        const access = await getSettingsAccess(localId, request.signal);
        if (!disposed && !request.signal.aborted) setState({ ...access, status: 'ready', localId, userId });
      } catch {
        if (!disposed && !request.signal.aborted) setState({ ...denied, status: 'error', localId, userId });
      } finally {
        if (!disposed && !request.signal.aborted) timer = setTimeout(load, STAFF_TIPS_REFRESH_MS);
      }
    };
    const visibility = () => {
      clearTimeout(timer);
      controller?.abort();
      setState({ ...denied, status: 'loading', localId, userId });
      if (!document.hidden) void load();
    };
    document.addEventListener('visibilitychange', visibility);
    void load();
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [localId, userId, superadmin]);
  if (!isFeatureRolloutLocal(localId) || superadmin || state.localId !== localId || state.userId !== userId) {
    return { ...denied, status: 'ready', localId, userId };
  }
  return state;
}
